import { IconArrowLeft, IconArrowRight, IconBuildingBank, IconFileText, IconInfoCircle, IconSearch } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useEsDueno } from "@/features/solicitudes/hooks/use-es-dueno";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { useSeleccionarOrdenDeCompra } from "@/features/seleccionar-orden-compra/hooks/use-seleccionar-orden-compra";
import { ordenCompraApi, type ConsultaOrden } from "@/features/seleccionar-orden-compra/services/orden-compra.api";
import { Badge, Button, Card, Group, Heading, IconAction, Input, PageHeader, ReadOnlyBanner, Stack, Text } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { toastErrorApi } from "@/lib/toast";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/orden-compra";
import { ConsultaOrdenModal } from "./ConsultaOrdenModal";

function formatMonto(monto: number, moneda: string): string {
  try {
    return new Intl.NumberFormat("es-MX", { style: "currency", currency: moneda }).format(monto);
  } catch {
    return `${monto.toFixed(2)} ${moneda}`;
  }
}

/**
 * Paso previo del flujo Orden de Compra (OC): el solicitante captura la clave de la
 * orden de compra (existe en SAP). Al asociarla se muestra el proveedor derivado y el
 * importe, y se habilita continuar a la carga del CFDI (que se conciliará contra las
 * entradas de almacén recibidas).
 */
export function SeleccionarOrdenDeCompraPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: sol } = useSolicitud(id);

  const [clave, setClave] = useState(sol.ordenCompra?.clave ?? "");
  const asociar = useSeleccionarOrdenDeCompra(id);

  // Visor de consulta (estilo ME23N) de la OC por su clave.
  const [consulta, setConsulta] = useState<ConsultaOrden | null>(null);
  const [consultando, setConsultando] = useState(false);
  const abrirConsulta = async () => {
    const c = clave.trim();
    if (!c) return;
    setConsultando(true);
    try {
      setConsulta(await ordenCompraApi.consultar(c));
    } catch (e) {
      toastErrorApi(e);
    } finally {
      setConsultando(false);
    }
  };

  // Asociar/cambiar la orden es exclusivo del dueño (P29: otros roles ven el
  // paso en solo lectura) y solo en los estados previos a la carga del CFDI.
  const esDueno = useEsDueno(sol);
  const puedeEditar = esDueno && (sol.estado === "SubtipoSeleccionado" || sol.estado === "OrdenDeCompraSeleccionada");

  const orden = sol.ordenCompra;
  // Invoice (factura extranjera) no tiene CFDI del SAT · el siguiente paso es cargar el PDF.
  const esInvoice = sol.subtipo === "INVOICE";

  return (
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title="Orden de compra"
        description="Orden de Compra · asocia la orden existente en SAP contra la que se paga."
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: id.slice(0, 8), href: `/solicitudes/${id}` },
          { label: "Orden de compra" },
        ]}
      />
      <LayoutConStepper estado={sol.estado} subtipo={sol.subtipo} tipo={sol.tipo} prorrateoAutomatico={sol.prorrateoAutomatico} paso="orden" solicitudId={id} contabilizada={sol.enviadaAContabilizacion} fillHeight>
        <Stack gap="md">
          {!esDueno && <ReadOnlyBanner motivo="no-dueno" mensaje="La orden de compra asociada no puede modificarse." />}
          <Card padding="lg">
            <Stack gap="md">
              <div>
                <Heading order={5}>Clave de la orden de compra</Heading>
                <Text size="sm" c="dimmed" className="mt-0.5">
                  Ingresa la clave de la orden de compra (ej. 4500079070). Se busca en SAP y se deriva el proveedor.
                </Text>
              </div>

              <Group align="flex-end" gap="sm">
                <Input label="Clave de la orden" placeholder="4500079070" value={clave} onChange={(e) => setClave(e.currentTarget.value)} readOnly={!puedeEditar} wrapperClassName="flex-1" />
                <IconAction label="Consultar orden de compra (solo lectura)" variant="light" size="lg" loading={consultando} disabled={!clave.trim()} onClick={abrirConsulta}>
                  <IconInfoCircle size={20} />
                </IconAction>
                {puedeEditar && (
                  <Button leftSection={<IconSearch size={14} />} loading={asociar.isPending} disabled={!clave.trim()} onClick={() => asociar.mutate(clave.trim())}>
                    {asociar.isPending ? "Buscando…" : "Asociar orden"}
                  </Button>
                )}
              </Group>

              {orden && (
                <Card
                  padding="md"
                  style={{ borderColor: "var(--color-multiva-400)", background: "color-mix(in srgb, var(--color-multiva-600) 6%, transparent)" }}
                >
                  <Stack gap={6}>
                    <Group gap={8}>
                      <IconFileText size={18} className="text-multiva-600" />
                      <Text fw={600}>Orden de compra {orden.clave}</Text>
                    </Group>
                    <Group gap={8}>
                      <IconBuildingBank size={16} />
                      <Text size="sm" fw={500}>
                        {orden.proveedorNombre}
                      </Text>
                      <Badge variant="light" color="slate">
                        #{orden.proveedorNumero}
                      </Badge>
                      <Text size="sm" c="dimmed">
                        {orden.proveedorRfc}
                      </Text>
                    </Group>
                    <Text size="sm">
                      Importe de la orden:{" "}
                      <Text span fw={600}>
                        {formatMonto(orden.total, orden.moneda)}
                      </Text>
                    </Text>
                    <Text size="xs" c="dimmed">
                      {esInvoice
                        ? "El Invoice (PDF) que cargues debe ser del mismo proveedor de la orden de compra."
                        : "El CFDI que cargues debe ser del mismo proveedor y sus conceptos deben corresponder a lo recibido en almacén (se concilia línea a línea por valor unitario, cantidad, unidad e IVA)."}
                    </Text>
                  </Stack>
                </Card>
              )}
            </Stack>
          </Card>

          <Group justify="space-between">
            <Button variant="default" leftSection={<IconArrowLeft size={14} />} onClick={() => navigate({ to: "/solicitudes/$id", params: { id } })}>
              Volver a la solicitud
            </Button>
            <Button
              rightSection={<IconArrowRight size={14} />}
              disabled={!orden}
              onClick={() =>
                navigate({
                  to: sol.subtipo === "INVOICE" ? "/solicitudes/$id/carga-invoice" : "/solicitudes/$id/cargar-cfdi",
                  params: { id },
                })
              }
            >
              {esInvoice ? "Continuar a carga del Invoice (PDF)" : "Continuar a carga de CFDI"}
            </Button>
          </Group>
        </Stack>
      </LayoutConStepper>

      <ConsultaOrdenModal orden={consulta} onClose={() => setConsulta(null)} />
    </div>
  );
}
